import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router';
import { listAccountEvents, rotuloDaIdentidade, type IdentityKind } from '../../services/banQueue';
import type { Event } from '../../services/events';
import { ordenarEvidencias, piorTom } from '../../lib/evidence';
import { EvidenceLine } from '../../components/EvidenceLine';
import {
  agruparPorPeriodo,
  BALDES_PADRAO,
  PERIOD_KEYS,
  rotuloDoPeriodo,
  type PeriodKey,
} from '../../lib/period';

// ============================================================
// Histórico da conta — "registros feitos nelas, organizados por dia/semana/mês"
// ============================================================
// A granularidade é um seletor e não uma tela por tipo: a mesma lista de
// registros vista em três resoluções. Dia responde "o que ele fez ontem",
// semana responde "isso é rotina ou foi uma noite", mês responde "há quanto
// tempo isso dura" — e é a resposta do mês que separa o curioso do reincidente
// na hora de escolher entre suspender e banir.
//
// BALDES VAZIOS NÃO APARECEM (agruparPorPeriodo). Trinta linhas "0 registros"
// entre duas detecções esconderiam as duas.
//
// ⚠️ AQUI APARECE TUDO, inclusive o que a fila de ban filtra fora.
// A fila só admite Strong+; esta tela mostra o registro inteiro da conta,
// contexto e ruído incluídos. É de propósito: a fila responde "quem banir", o
// histórico responde "o que essa pessoa fez", e a segunda pergunta é a que
// desfaz um falso positivo.
// ============================================================

const KINDS: IdentityKind[] = ['account_id', 'user_id'];

export function AccountDetail() {
  const { kind, value } = useParams<{ kind: string; value: string }>();
  const [period, setPeriod] = useState<PeriodKey>('day');
  const [eventos, setEventos] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const kindValido = !!kind && KINDS.includes(kind as IdentityKind);

  useEffect(() => {
    if (!kindValido || !value) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    listAccountEvents({ kind: kind as IdentityKind, value, period })
      .then((r) => { if (!cancelled) setEventos(r.data ?? []); })
      .catch((e) => {
        if (!cancelled) {
          setEventos([]);
          setError(e instanceof Error ? e.message : String(e));
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [kind, value, period, kindValido]);

  const baldes = useMemo(
    () => agruparPorPeriodo(eventos, (e) => e.last_seen_at || e.created_at, period),
    [eventos, period],
  );

  if (!kind || !value) {
    return <div className="text-red-400">Falta tipo/valor na URL.</div>;
  }
  if (!kindValido) {
    return (
      <div className="text-red-400">
        Tipo inválido: {kind} (esperado: account_id | user_id)
      </div>
    );
  }

  return (
    <div>
      <header className="mb-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-2xl font-bold text-gray-200 break-all">
              <span className="text-gray-500 text-base font-normal">
                {rotuloDaIdentidade(kind as IdentityKind)}{' '}
              </span>
              <span className="font-mono">{value}</span>
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Registros dos últimos {BALDES_PADRAO[period]}{' '}
              {rotuloDoPeriodo(period).toLowerCase()}(s), agrupados por{' '}
              {rotuloDoPeriodo(period).toLowerCase()}. Atribuição por ÉPOCA: só
              entra o que foi observado enquanto esta identidade estava na sessão.
            </p>
          </div>

          <div className="flex items-center gap-2">
            {PERIOD_KEYS.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setPeriod(k)}
                className={`px-3 py-1.5 text-sm rounded border ${
                  period === k
                    ? 'border-red-700 bg-red-950/40 text-red-300'
                    : 'border-[#2a2f36] bg-[#161a1f] text-gray-300 hover:bg-gray-800'
                }`}
              >
                {rotuloDoPeriodo(k)}
              </button>
            ))}
          </div>
        </div>

        {/* A investigação de reputação/cross-correlação já existe e aceita
            account_id — não vale reimplementar aqui. Para `user_id` o endpoint
            não tem tipo correspondente ({ip, hwid, account_id}), então o link
            não é oferecido, em vez de levar a um 404. */}
        {kind === 'account_id' && (
          <Link
            to={`/investigations/account_id/${encodeURIComponent(value)}`}
            className="inline-block mt-3 text-xs text-blue-400 hover:underline"
          >
            🔍 abrir investigação (reputação, IPs e máquinas ligadas)
          </Link>
        )}
      </header>

      {error && (
        <div className="bg-red-950 border border-red-800 text-red-300 text-sm rounded px-3 py-2 mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-gray-500">Carregando...</p>
      ) : baldes.length === 0 ? (
        <p className="text-gray-500 text-sm">Nenhum registro nesta janela.</p>
      ) : (
        <div className="space-y-4">
          {baldes.map((balde) => {
            const ordenados = ordenarEvidencias(balde.itens);
            const tom = piorTom(balde.itens);
            const sevMax = balde.itens.reduce((m, e) => Math.max(m, e.severity ?? 0), 0);
            const repeticoes = balde.itens.reduce((s, e) => s + (e.occurrence_count ?? 1), 0);
            return (
              <section key={balde.inicio}>
                <div className="flex flex-wrap items-baseline gap-2 mb-1">
                  <h3 className="text-sm font-bold text-gray-200">{balde.rotulo}</h3>
                  <span className="text-xs text-gray-500 tabular-nums">
                    {balde.itens.length} registro(s)
                    {repeticoes > balde.itens.length && ` · ${repeticoes} repetições`}
                    {sevMax > 0 && ` · sev máx ${sevMax}`}
                  </span>
                  {tom === 'critico' && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded border border-red-700 bg-red-950/50 text-red-300">
                      alteração confirmada neste período
                    </span>
                  )}
                </div>
                <div className="bg-[#161a1f] border border-[#2a2f36] rounded divide-y divide-[#2a2f36]">
                  {ordenados.map((ev, i) => (
                    <div key={ev.id ?? `${balde.inicio}-${i}`} className="p-2.5">
                      <EvidenceLine evento={ev} />
                      {ev.identity_epoch !== undefined && ev.identity_epoch > 0 && (
                        <p
                          className="text-[10px] text-gray-600 mt-1"
                          title="A sessão trocou de identidade; este registro é da época em que esta conta estava logada."
                        >
                          época {ev.identity_epoch} da sessão
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default AccountDetail;
